import { IEnergyEmission } from '../../../../../../../../hooks/api/energy-emissions';
import { DeclaredUnit } from '../../../../../../../../shared/interfaces/IComponentDetails';

export enum PROCESS_DROPDOWN_OPTIONS {
  INJECTION_MOLDING = 'Injection Molding Process',
  INDIVIDUAL_PROCESS = 'Individual Process',
  TRANSPORT_PROCESS = 'Transport Process',
}

// Forward mapping: enum value → key
export const ProcessIdsMap: Record<PROCESS_DROPDOWN_OPTIONS, string> = {
  [PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]: 'INJECTION_MOLDING',
  [PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]: 'INDIVIDUAL_PROCESS',
  [PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]: 'TRANSPORT_PROCESS',
};
export enum ActionType {
  ADD = 'add',
}

export enum MachineType {
  MAIN = 'main',
  AUXILIARY = 'auxiliary',
}

export interface EnergySourceEmissionFactor extends IEnergyEmission {
  pcfIncludingBiogenic: number;
  location: string;
}

/** One row of an Individual Process's "Energy Input" section (PAC-1617). */
export type IEnergySourceInput = {
  source?: ENERGY_SOURCE | null;
  amount?: number | null;
  energy_source_details?: EnergySourceEmissionFactor;
};

export type IMachine = {
  type: MachineType;
  name?: string;
  operation_mode?: OperationMode;
  manufacturer_id?: number;
  model?: string;
  rated_power: number;
  shot_weight?: number;
  cycle_time?: number;
  operation_value?: number;
  number_of_cavities?: number;
  action?: ActionType;
  energy_sources?: IEnergySourceInput[];
  comment?: string;
  energy_per_piece?: number;
};

export enum SprueTreatmentSelection {
  REUSED_IN_SAME_MACHINE = 'REUSED_IN_SAME_MACHINE',
  DISCARDED = 'DISCARDED',
}

export enum WASTE_TREATMENT_CATEGORIES {
  PLASTIC = 'Plastic',
  STEEL = 'Steel',
  OTHER = 'Other',
}

export type IWasteTreatment = {
  id: number;
  name: string;
  category: WASTE_TREATMENT_CATEGORIES;
  location: string;
  referencePeriod: number;
  pcfExcludingBiogenic: number;
  pcfIncludingBiogenic: number;
  source: string;
  comment: string;
  allowedProcesses: PROCESS_DROPDOWN_OPTIONS[];
};

export type IWasteManagement = {
  sprue_weight?: number;
  sprue_treatment_type?: SprueTreatmentSelection;
  sprue_waste_treatment?: IWasteTreatment;
  scrap_waste_treatment?: IWasteTreatment;
  scrap_rate?: number;
  emissions?: number;
};

export type IInputMaterial = {
  id?: number;
  material_name?: string;
  product_cid?: string;
  pcf_including_biogenic?: number;
  pcf_excluding_biogenic?: number;
  unitary_product_amount?: number;
  declared_unit?: DeclaredUnit;
  quality?: string;
  source?: string;
  comment?: string;
  amount_per_piece?: number | null;
  waste_per_piece?: number | null;
  waste_treatment?: IWasteTreatment | null;
  waste_treatment_id?: number | null;
  emissions?: number;
  fossil_ghg_emissions?: number;
  fossil_carbon_content?: number;
  carbon_content_biogenic?: number;
  product_mass_per_declared_unit?: number;
};

export enum OperationMode {
  HANDLING_TIME = 'handling_time',
  RUNS_PER_CYCLE = 'runs_per_cycle',
  DEFAULT = 'default',
}

export const OperationModeMap: Record<OperationMode, string> = {
  [OperationMode.HANDLING_TIME]: 'handling_time',
  [OperationMode.RUNS_PER_CYCLE]: 'runs_per_cycle',
  [OperationMode.DEFAULT]: 'default',
};

export interface IManufacturer {
  id: number;
  name: string;
  dateCreated: string;
  dateUpdated: string;
}

export enum ENERGY_SOURCE {
  ELECTRICITY = 'ELECTRICITY',
  COMPRESSED_AIR = 'COMPRESSED_AIR',
  GAS = 'GAS',
  FUEL = 'FUEL',
}

export const EnergySourceUnitMap: Record<ENERGY_SOURCE, DeclaredUnit> = {
  [ENERGY_SOURCE.ELECTRICITY]: DeclaredUnit.kilowattHour,
  [ENERGY_SOURCE.COMPRESSED_AIR]: DeclaredUnit.cubicMeter,
  [ENERGY_SOURCE.GAS]: DeclaredUnit.kilowattHour,
  [ENERGY_SOURCE.FUEL]: DeclaredUnit.kilowattHour,
};

export const EnergySourceTranslationKeyMap: Record<ENERGY_SOURCE, string> = {
  [ENERGY_SOURCE.ELECTRICITY]:
    'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.energySourceOptions.electricity',
  [ENERGY_SOURCE.COMPRESSED_AIR]:
    'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.energySourceOptions.compressedAir',
  [ENERGY_SOURCE.GAS]:
    'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.energySourceOptions.gas',
  [ENERGY_SOURCE.FUEL]:
    'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.energySourceOptions.fuel',
};

export enum ActionStatus {
  SAVE = 'save',
  CALCULATE = 'calculate',
}
